///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { UserAuthorities } from '../../services/user/user-authorities';
import { GtthProjectInfoViewService } from './project-info-view.service';
import { Principal } from '../../shared/auth/principal.service';
import { GtthRunningDesignInfoService } from './running-design-info.service';
import { GtthNotificationsService } from '../../shared/notifications/notifications.service';
import { E2eTestUtils } from '../../../testing/e2e-test-utils';
import { NgClass } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { SuiTooltipDirective } from '@siklu/sui-lib/infrastructure';
import { SuiSvgIconComponent } from '@siklu/sui-lib/components';
import { SuiTranslatePipe } from '@siklu/sui-lib/localization';
import { MatButtonModule } from '@angular/material/button';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GtthNotificationsComponent } from '../../shared/notifications/notifications.component';
import { FormsModule } from '@angular/forms';
import { LoginModalComponent } from '../../shared/login/login.component';
import { HasAnyAuthorityDirective } from '../../shared/auth/has-any-authority.directive';
import { ButtonsDirectivesModule } from '../../shared/directives/buttons-directives/buttons-directives.module';
import { MatDialog } from '@angular/material/dialog';
import { AboutWindeDialogComponent } from '../../global-dialogs/about-winde/about-winde-dialog.component';

@Component({
    selector: 'winde-navbar',
    templateUrl: './navbar.component.html',
    styleUrls: ['navbar.scss'],
    imports: [
        MatMenuModule,
        MatButtonModule,
        FormsModule,
        RouterModule,
        ButtonsDirectivesModule,
        SuiSvgIconComponent,
        GtthNotificationsComponent,
        LoginModalComponent,
        SuiTranslatePipe,
        SuiTooltipDirective,
        HasAnyAuthorityDirective,
        NgClass
    ]
})
export class NavbarComponent implements OnInit {
    public readonly tooltipsAreDisabled = E2eTestUtils.isTooltipsDisabled();
    userAuthorities = UserAuthorities;

    public readonly SikluMainUrl = 'https://www.siklu.com/';
    stateIndicatorIconClass: string;

    private readonly destroyRef = inject(DestroyRef);

    constructor(
        private readonly principal: Principal,
        private readonly router: Router,
        public projectInfoService: GtthProjectInfoViewService,
        private readonly runningDesignInfoService: GtthRunningDesignInfoService,
        public notificationsService: GtthNotificationsService,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.principal.identity();

        this.principal.getAuthenticationState().subscribe((authenticationState) => {
            if (!authenticationState) {
                return;
            }
            this.principal.hasAnyAuthority(UserAuthorities.PROJECT_EDIT).then((hasAuthority: boolean) => {
                if (hasAuthority) {
                    this.runningDesignInfoService
                        .getIsAnyDesignRunning()
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe((isDesignRunning: boolean) => {
                            this.stateIndicatorIconClass = isDesignRunning ? 'refresh' : '';
                        });
                }
            });
        });
    }

    showNotificationsWindow(showNotifications: boolean): void {
        if (this.notificationsService.showNotifications !== showNotifications) {
            this.notificationsService.showNotifications = showNotifications;
            /*  if notifications window is open do refresh notifications every 5sec., else refresh every 60sec. */
            this.runningDesignInfoService.setRefreshNotificationsInterval$(
                showNotifications
                    ? GtthRunningDesignInfoService.FREQUENT_REFRESH_TIMEOUT
                    : GtthRunningDesignInfoService.ORDINARY_REFRESH_TIMEOUT
            );
        }
    }

    isAuthenticated() {
        return this.principal.isAuthenticated();
    }

    logout() {
        this.principal.forceLogout();
    }

    reportProblem() {
        this.router.navigate([{ outlets: { popup: 'feedback/report-problem' } }], { queryParamsHandling: 'preserve' });
    }

    showAbout() {
        this.dialog.open(AboutWindeDialogComponent, AboutWindeDialogComponent.DIALOG_CONFIG);
    }
}
